import type { GeocodeResult } from '@map/shared';

/**
 * Partie pure de la recherche de lieu : anti-rebond, mise en forme des
 * resultats, deplacement du curseur clavier.
 *
 * Tout ce qui est testable vit ici, hors de React. Le composant ne garde que
 * l'etat et le balisage.
 */

/** En dessous de deux caracteres, la recherche ramenerait le monde entier. */
export const SEARCH_MIN_LENGTH = 2;

/**
 * 400 ms : au-dessus de la duree entre deux frappes d'une saisie continue, en
 * dessous du seuil ou l'attente se remarque. C'est aussi ce qui protege le
 * quota Nominatim : « samarcande » part en une requete, pas en dix.
 */
export const SEARCH_DEBOUNCE_MS = 400;

export function isSearchable(query: string): boolean {
  return query.trim().length >= SEARCH_MIN_LENGTH;
}

export interface Debounced<A extends readonly unknown[]> {
  (...args: A): void;
  /** Annule l'appel en attente. Indispensable au demontage du composant. */
  cancel(): void;
}

/**
 * Ne laisse passer que le dernier appel d'une rafale.
 *
 * Chaque appel repousse l'echeance : c'est le comportement attendu d'une barre
 * de recherche, ou seule la frappe finale a un sens.
 */
export function debounce<A extends readonly unknown[]>(
  action: (...args: A) => void,
  delayMs: number,
): Debounced<A> {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const debounced = (...args: A): void => {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      action(...args);
    }, delayMs);
  };

  debounced.cancel = (): void => {
    if (timer === null) return;
    clearTimeout(timer);
    timer = null;
  };

  return debounced;
}

export interface SearchEntry {
  /** Clef de liste : deux resultats peuvent partager un libelle, jamais un point. */
  readonly key: string;
  /** Le nom du lieu, mis en avant. */
  readonly primary: string;
  /** Le contexte (region, pays), en retrait. Vide si Nominatim n'en donne pas. */
  readonly secondary: string;
  readonly kind: string;
  readonly lat: number;
  readonly lng: number;
  /** Zoom d'arrivee, choisi selon l'echelle du lieu. */
  readonly zoom: number;
}

/**
 * Zoom d'arrivee par nature de lieu.
 *
 * Arriver au zoom 16 sur un pays ne montre qu'un champ ; arriver au zoom 6 sur
 * un col de montagne ne montre rien d'utile. L'echelle du cadrage doit suivre
 * l'echelle de l'objet cherche.
 */
const ZOOM_BY_KIND: Record<string, number> = {
  country: 5,
  state: 7,
  region: 7,
  province: 7,
  county: 8,
  sea: 6,
  desert: 6,
  city: 11,
  municipality: 11,
  island: 9,
  lake: 10,
  water: 10,
  bay: 10,
  river: 10,
  town: 12,
  village: 13,
  hamlet: 14,
  suburb: 13,
  neighbourhood: 14,
  peak: 13,
  mountain_pass: 13,
  glacier: 12,
  attraction: 15,
  tourism: 15,
  hotel: 16,
  building: 17,
  road: 15,
  house: 18,
};

/** Sans nature reconnue, un cadrage de ville : le cas le plus frequent. */
const DEFAULT_ZOOM = 12;

export function zoomForKind(kind: string): number {
  return ZOOM_BY_KIND[kind.toLowerCase()] ?? DEFAULT_ZOOM;
}

/**
 * Coupe le libelle Nominatim en deux : le nom, puis son contexte.
 *
 * `display_name` est une adresse complete, du plus precis au pays. Affichee
 * telle quelle sur une ligne, elle deborde et le nom cherche se noie au milieu
 * de trois divisions administratives.
 */
export function splitLabel(label: string): { primary: string; secondary: string } {
  const comma = label.indexOf(',');
  if (comma === -1) return { primary: label.trim(), secondary: '' };
  return {
    primary: label.slice(0, comma).trim(),
    secondary: label
      .slice(comma + 1)
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part.length > 0)
      .join(' · '),
  };
}

/** Met en forme la reponse du serveur pour l'affichage, en preservant l'ordre. */
export function toSearchEntries(results: readonly GeocodeResult[]): SearchEntry[] {
  return results.map((result) => {
    const { primary, secondary } = splitLabel(result.label);
    return {
      key: `${result.lat},${result.lng},${result.kind}`,
      primary: primary.length > 0 ? primary : result.label,
      secondary,
      kind: result.kind,
      lat: result.lat,
      lng: result.lng,
      zoom: zoomForKind(result.kind),
    };
  });
}

/**
 * Deplace le curseur clavier dans la liste.
 *
 * Le curseur boucle : arrive en bas, la fleche du bas revient en haut. Sur une
 * liste de huit lignes c'est plus rapide que de remonter une a une. `-1`
 * signifie « aucune ligne visee », etat de depart tant que l'utilisateur tape.
 */
export function nextHighlight(current: number, count: number, delta: number): number {
  if (count <= 0) return -1;
  if (current < 0) return delta > 0 ? 0 : count - 1;
  return (current + delta + count) % count;
}
